import { useApolloClient, useMutation } from '@apollo/client';
import { useEventCallback } from '@mui/material/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  catchError,
  defer,
  EMPTY,
  firstValueFrom,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { translateInput } from '@work4all/data/lib/hooks/data-provider';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { InputErpAnhangAttachementsRelation } from '@work4all/models/lib/Classes/InputErpAnhangAttachementsRelation.entity';
import { ModifyShadowBzObjectResult } from '@work4all/models/lib/Classes/ModifyShadowBzObjectResult.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';
import { InsertBomStyle } from '@work4all/models/lib/Enums/InsertBomStyle.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { AccesorsOf } from '@work4all/utils/lib/paths-of/paths-of';

import {
  ApiErrors,
  ValidationErrors,
} from '../../../../../../apollo/ValidationErrors';
import { NEW_ENTITY_ID } from '../../../../mask-metadata';
import { OnEditPosition } from '../../components/tab-panels/positions/components/edit-table/types';
import { ErpData } from '../../ErpData';
import { createTransaction } from '../../sentry/hooks/use-transaction';

import { IUseShadowObjectApiOptions } from './IUseShadowObjectApiOptions';
import {
  createShadowBzObjectGraphQl,
  CreateShadowBzObjectResponse,
  CreateShadowBzObjectVars,
  DELETE_SHADOW_BZ_OBJECT,
  DeleteShadowBzObjectResponse,
  DeleteShadowBzObjectVars,
  modifyShadowBzObjectAddPositionGraphQl,
  ModifyShadowBzObjectAddPositionResponse,
  ModifyShadowBzObjectAddPositionVars,
  modifyShadowBzObjectGraphQl,
  modifyShadowBzObjectModifyPositionGraphQl,
  ModifyShadowBzObjectModifyPositionResponse,
  ModifyShadowBzObjectModifyPositionVars,
  ModifyShadowBzObjectMovePositionResponse,
  ModifyShadowBzObjectMovePositionVars,
  modifyShadowBzObjectRemovePositionGraphQl,
  ModifyShadowBzObjectRemovePositionResponse,
  ModifyShadowBzObjectRemovePositionVars,
  ModifyShadowBzObjectResponse,
  ModifyShadowBzObjectVars,
  modifyShadowObjectMovePositionGraphQl,
  persistShadowBzObjectGraphQl,
  PersistShadowBzObjectResponse,
  PersistShadowBzObjectVars,
} from './use-shadow-bz-object-graphql';

export type PositionAccessors = AccesorsOf<Position>;

export const ERP_TEXT_CELLS: PositionAccessors[] = [
  'longtext',
  'unit',
  'insteadOfTotalPrice',
  'articleNumber',
  'measurement',
  'ownArticleNumber',
  'ledgerAccountNumber',
  'manufacturerNumber',
  'shortText',
  'costCenter1Number',
  'internalText',
];
export const ERP_DATE_CELLS: PositionAccessors[] = [
  'dispositionStart',
  'dispositionEnd',
  'deliveryDate',
  'actualDeliveryDate',
];
export const ERP_TIME_CELLS: PositionAccessors[] = ['startTime', 'endTime'];
export const ERP_NUMBER_CELLS: PositionAccessors[] = [
  'amount',
  'singlePriceNet',
  'discount',
  'purchasePrice',
  'vat',
  'width',
  'weight',
  'length',
  'amountCarton',
  'amoundPalettes',
  'amountVe',
  'purchasePriceSurcharge',
  'factor',
  'minutePrice',
];

export interface ShadowObjectAddPositionArgs {
  positionType: ErpPositionsKind;
  article?: Article | null;
  bomStyle?: InsertBomStyle;
}

export type ValidationErrors = ApiErrors<ErpData>;

interface PendingChangePosition {
  positionId: number;
}

interface PendingChangeAddModifyPosition extends PendingChangePosition {
  type: 'modifyPosition';
  properties?: string[];
}

interface PendingChangeMovePosition extends PendingChangePosition {
  type: 'movePosition';
}
interface PendingChangeNone {
  type: 'none';
}

interface PendingChangeAddPosition {
  type: 'addPosition';
}

interface PendingChangeModify {
  type: 'modify';
}

type PendingChange =
  | (
      | PendingChangeModify
      | PendingChangeNone
      | PendingChangeAddModifyPosition
      | PendingChangeMovePosition
      | PendingChangeAddPosition
    ) & {
      pending: boolean;
    };

const PENDING_NONE: PendingChange = {
  pending: false,
  type: 'none',
};

export function useShadowBzObjectApi(options: IUseShadowObjectApiOptions) {
  const { entity, id, parentEntity, parentId, skip, contactId } = options;

  const client = useApolloClient();

  const [errors, setErrors] = useState<ValidationErrors>([]);
  const [shadowBzObject, setShadowBzObject] =
    useState<ModifyShadowBzObjectResult | null>(null);

  const [deferredQueue, setDeferredQueue] = useState<Subject<
    Observable<ModifyShadowBzObjectResult>
  > | null>(() => new Subject());
  const [immediateQueue, setImmediateQueue] = useState<Subject<
    Observable<ModifyShadowBzObjectResult>
  > | null>(() => new Subject());
  const [stop, setStop] = useState<Subject<void> | null>(() => new Subject());
  const [stopped, setStopped] = useState<Subject<void> | null>(
    () => new Subject()
  );

  const [entityId, setEntityId] = useState(null);

  const [createLoading, setCreateLoading] = useState(false);
  // Only changes not first load
  const [pending, setPending] = useState<PendingChange>(PENDING_NONE);
  const [isDirtyForm, setIsDirty] = useState(false);
  const [signature, setSignature] = useState<string>();
  const isDirty = isDirtyForm || Boolean(signature);
  const init = useCallback(() => {
    unstable_batchedUpdates(() => {
      setDeferredQueue(new Subject());
      setImmediateQueue(new Subject());
      setStop(new Subject());
      setStopped(new Subject());

      setIsDirty(false);
    });
  }, []);

  const [mutateCreate] = useMutation<
    CreateShadowBzObjectResponse,
    CreateShadowBzObjectVars
  >(
    useMemo(() => createShadowBzObjectGraphQl(entity), [entity]),
    {
      onError() {
        setCreateLoading(false);
      },
      onCompleted(response) {
        setCreateLoading(false);
        setShadowBzObject(response.createShadowBzObject);
        init();
      },
    }
  );

  const [mutateModify] = useMutation<
    ModifyShadowBzObjectResponse,
    ModifyShadowBzObjectVars
  >(useMemo(() => modifyShadowBzObjectGraphQl(entity), [entity]));

  const [mutateAddPosition] = useMutation<
    ModifyShadowBzObjectAddPositionResponse,
    ModifyShadowBzObjectAddPositionVars
  >(useMemo(() => modifyShadowBzObjectAddPositionGraphQl(entity), [entity]));

  const [mutateMovePosition] = useMutation<
    ModifyShadowBzObjectMovePositionResponse,
    ModifyShadowBzObjectMovePositionVars
  >(useMemo(() => modifyShadowObjectMovePositionGraphQl(entity), [entity]));

  const [mutateModifyPosition] = useMutation<
    ModifyShadowBzObjectModifyPositionResponse,
    ModifyShadowBzObjectModifyPositionVars
  >(useMemo(() => modifyShadowBzObjectModifyPositionGraphQl(entity), [entity]));

  const [mutateRemovePosition] = useMutation<
    ModifyShadowBzObjectRemovePositionResponse,
    ModifyShadowBzObjectRemovePositionVars
  >(useMemo(() => modifyShadowBzObjectRemovePositionGraphQl(entity), [entity]));

  const [mutatePersist] = useMutation<
    PersistShadowBzObjectResponse,
    PersistShadowBzObjectVars
  >(
    useMemo(() => persistShadowBzObjectGraphQl(entity), [entity]),
    {
      onCompleted(value: PersistShadowBzObjectResponse) {
        setEntityId(value.persistShadowBzObject.data.id);
        setSignature('');
        // Clears out the cache and then re-executes all active queries.
        // This will ensure no stale data is displayed.
        client.resetStore();
      },
    }
  );

  const [mutateDelete] = useMutation<
    DeleteShadowBzObjectResponse,
    DeleteShadowBzObjectVars
  >(DELETE_SHADOW_BZ_OBJECT);

  useEffect(() => {
    if (!deferredQueue || !immediateQueue || !stop) return;

    const subscription = merge(
      deferredQueue.pipe(
        tap(() => setIsDirty(true)),
        takeUntil(stop),
        switchMap((request) => {
          return timer(250).pipe(map(() => request));
        })
      ),
      immediateQueue.pipe(
        tap(() => setIsDirty(true)),
        takeUntil(stop)
      )
    )
      .pipe(
        switchMap((request) => {
          return request.pipe(
            takeUntil(merge(deferredQueue, immediateQueue)),
            catchError(() => EMPTY)
          );
        }),
        tap((result) => {
          setIsDirty(true);
          setShadowBzObject(result);
        })
      )
      .subscribe({
        complete() {
          stopped.next();
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [deferredQueue, immediateQueue, stop, stopped]);

  useEffect(() => {
    const bzObjectType = convertEntityToBzObjType(entity);

    const variables: CreateShadowBzObjectVars =
      id == null || id === NEW_ENTITY_ID
        ? {
            type: bzObjectType,
            sdObjType: convertEntityToSdObjType(parentEntity),
            sdObjMemberCode: Number(parentId),
            apCode: contactId,
          }
        : {
            type: bzObjectType,
            bzObjMemberCode: Number(id),
            apCode: contactId,
          };

    if (!skip) {
      setCreateLoading(true);
      mutateCreate({ variables });
    }
  }, [mutateCreate, entity, id, parentEntity, parentId, skip, contactId]);

  useEffect(() => {
    const id = shadowBzObject?.id;

    if (id) {
      return () => {
        mutateDelete({ variables: { id } });
      };
    }
  }, [mutateDelete, shadowBzObject?.id]);

  const modify = useEventCallback((value) => {
    if (!shadowBzObject) return;

    const { id } = shadowBzObject;

    deferredQueue.next(
      defer(() => {
        const transaction = createTransaction({
          name: 'ERP Mask - Modify position',
        });
        setPending({
          pending: true,
          type: 'modify',
        });
        return mutateModify({
          variables: { id, [entity]: translateInput(entity, value) },
          onCompleted: () => {
            transaction?.finish();

            setErrors([]);
            setPending(PENDING_NONE);
          },
          onError: (e) => {
            const parsedErrors = ValidationErrors.parseErrors<ErpData>(
              e.graphQLErrors,
              entity
            );
            setErrors(parsedErrors);
            transaction?.finish();
            setPending(PENDING_NONE);
          },
        }).then(({ data }) => data.modifyShadowBzObject);
      })
    );
  });

  const persist = useEventCallback(
    async (attachements?: InputErpAnhangAttachementsRelation) => {
      if (!shadowBzObject) return;
      const relations = {
        setSignatureFromTempFile: signature,
      };

      const promise = firstValueFrom(
        stopped.pipe(
          switchMap(async () => {
            const transaction = createTransaction({
              name: 'ERP Mask - Persist',
            });
            await mutatePersist({
              variables: { id: shadowBzObject.id, attachements, relations },
              onCompleted: () => transaction?.finish(),
              onError: () => transaction?.finish(),
            });
          }),
          tap(() => {
            init();
          })
        )
      );

      stop.next();

      await promise;
    }
  );

  const addPosition = useEventCallback((args: ShadowObjectAddPositionArgs) => {
    const { positionType, article, bomStyle } = args;
    const articleId = article?.id;
    if (!shadowBzObject) return;

    const { id } = shadowBzObject;

    deferredQueue.next(
      defer(() => {
        const transaction = createTransaction({
          name: 'ERP Mask - Add Position',
        });
        setPending({ pending: true, type: 'addPosition' });
        return mutateAddPosition({
          variables: {
            id,
            positionType,
            articleId,
            bomStyle,
          },
          onCompleted: () => {
            transaction?.finish();
            setPending(PENDING_NONE);
          },
          onError: () => {
            transaction?.finish();
            setPending(PENDING_NONE);
          },
        }).then(({ data }) => data.modifyShadowBzObjectAddPosition);
      })
    );
  });

  const movePosition = useEventCallback((positionId: number, index: number) => {
    if (!shadowBzObject) return;

    const { id } = shadowBzObject;

    deferredQueue.next(
      defer(() => {
        const transaction = createTransaction({
          name: 'ERP Mask - Move Position',
        });
        setPending({ pending: true, type: 'movePosition', positionId });
        return mutateMovePosition({
          variables: {
            id,
            positionId,
            index,
          },
          onCompleted: () => {
            transaction?.finish();
            setPending(PENDING_NONE);
          },
          onError: () => {
            transaction?.finish();
            setPending(PENDING_NONE);
          },
        }).then(({ data }) => data.modifyShadowBzObjectMovePosition);
      })
    );
  });

  const removePosition = useEventCallback((positionIds: number[]) => {
    if (!shadowBzObject) return;

    const { id } = shadowBzObject;

    deferredQueue.next(
      defer(() =>
        mutateRemovePosition({
          variables: {
            id,
            positionId: positionIds[0],
            positionIds: positionIds.slice(1),
          },
        }).then(({ data }) => data.modifyShadowBzObjectRemovePosition)
      )
    );
  });

  const editPosition = useEventCallback((result: OnEditPosition<Position>) => {
    if (!shadowBzObject) return;

    const { id } = shadowBzObject;
    const { position } = result;

    const request: Partial<Position> = {};

    Object.entries(position).forEach((x) => {
      const [field, value] = x;
      if (
        ERP_NUMBER_CELLS.includes(field as PositionAccessors) &&
        typeof value === 'string'
      )
        request[field] = parseFloat(value);
      else request[field] = value;
    });
    if (Object.entries(request).length) {
      delete position.__typename;
      deferredQueue.next(
        defer(() => {
          const transaction = createTransaction({
            name: 'ERP Mask - Modify Position',
          });
          setPending({
            pending: true,
            type: 'modifyPosition',
            positionId: position.id,
            properties: Object.keys(position),
          });
          return mutateModifyPosition({
            variables: {
              id,
              position: translateInput<Position>(Entities.position, {
                id: position.id,
                ...request,
              }),
            },
            onCompleted: () => {
              transaction?.finish();
              setPending(PENDING_NONE);
            },
            onError: () => {
              transaction?.finish();
              setPending(PENDING_NONE);
            },
          }).then(({ data }) => {
            return data.modifyShadowBzObjectModifyPosition;
          });
        })
      );
    }
  });

  return [
    shadowBzObject,
    useMemo(
      () => ({
        entityId,
        loading: createLoading,
        isDirty,
        persist,
        modify,
        pending,
        addPosition,
        movePosition,
        editPosition,
        removePosition,
        setSignature,
      }),
      [
        entityId,
        createLoading,
        isDirty,
        persist,
        modify,
        pending,
        addPosition,
        movePosition,
        editPosition,
        removePosition,
        setSignature,
      ]
    ),
    errors,
  ] as const;
}

export function convertEntityToBzObjType(entity: Entities): BzObjType {
  switch (entity) {
    case Entities.calculation:
      return BzObjType.KALKULATION;
    case Entities.contract:
      return BzObjType.AUFTRAG;
    case Entities.offer:
      return BzObjType.ANGEBOT;
    case Entities.invoice:
      return BzObjType.RECHNUNG;
    case Entities.deliveryNote:
      return BzObjType.LIEFERSCHEIN;
    case Entities.order:
      return BzObjType.BESTELLUNG;
    case Entities.demand:
      return BzObjType.BEDARFSANFORDERUNG;
    case Entities.inboundDeliveryNote:
      return BzObjType.EINGANGSLIEFERSCHEIN;
    default:
      throw new Error(`Cannot convert entity "${entity}" tp \`BzObjType\``);
  }
}

function convertEntityToSdObjType(entity: Entities): SdObjType {
  switch (entity) {
    case Entities.customer:
      return SdObjType.KUNDE;
    case Entities.supplier:
      return SdObjType.LIEFERANT;
    case Entities.project:
      return SdObjType.PROJEKT;
    default:
      throw new Error(`Cannot convert entity "${entity}" to \`SdObjType\``);
  }
}
